import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Ban, Clock, CreditCard, Pencil, UserMinus } from "lucide-react";
import { Profile } from "@/hooks/profile/types";
import { PaymentStatus } from "@/hooks/admin/useUserPayments";
import { DebtInfo } from "@/hooks/admin/useMemberDebts";
import { PaymentForm } from "./payments/PaymentForm";
import { EditUserDialog } from "./EditUserDialog";
import { UserDetailDialog } from "./UserDetailDialog";
import { ResetPasswordButton } from "./user-row/ResetPasswordButton";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isAfter } from "date-fns";
import { DebtActionButton } from "@/components/admin/debts/DebtActionButton";

interface UserCardProps {
  user: Profile;
  onBanUser: (userId: string, banned: boolean) => void;
  onDeleteClick: (user: Profile) => void;
  onRoleChange: (userId: string, newRole: string) => void;
  currentUser?: Profile | null;
  paymentStatus?: PaymentStatus;
  /** True when the current user has the recepcionista role (via has_role),
   *  even if their primary profile.role is different (e.g. profesor with dual access). */
  actsAsRecepcionista?: boolean;
  isMastergym?: boolean;
  debtInfo?: DebtInfo;
}

const getRoleColor = (role: string) => {
  switch (role) {
    case "admin": return "bg-red-500";
    case "manager": return "bg-blue-500";
    case "profesor": return "bg-green-500";
    default: return "bg-gray-500";
  }
};

const getRoleLabel = (role: string) => {
  switch (role) {
    case "admin": return "Administrador";
    case "manager": return "Manager";
    case "profesor": return "Profesor";
    default: return "Socio";
  }
};

const getPaymentLabel = (status?: PaymentStatus) => {
  if (status === "upToDate") return { label: "Al día", class: "bg-green-500/10 text-green-500" };
  if (status === "expired") return { label: "Vencida", class: "bg-red-500/10 text-red-500" };
  return { label: "Sin pagos", class: "bg-red-500/10 text-red-500" };
};

export const UserCard = ({
  user,
  onBanUser,
  onDeleteClick,
  onRoleChange,
  currentUser,
  paymentStatus,
  actsAsRecepcionista = false,
  isMastergym = false,
  debtInfo,
}: UserCardProps) => {
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const isRecepcionista = actsAsRecepcionista || currentUser?.role === "recepcionista";
  const canPerformActions = isAdmin || (isManager && !!user.gym_id);
  const canRegisterAndEdit = canPerformActions || (isRecepcionista && !!user.gym_id);
  const isPending = user.isPending === true;

  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.username || "Usuario sin nombre";

  const debtBadge = isMastergym && debtInfo
    ? debtInfo.visualStatus === "overdue"
      ? { label: "En deuda VENCIDO", class: "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40" }
      : { label: "En deuda", class: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40" }
    : null;

  const payment = debtBadge ?? getPaymentLabel(paymentStatus);

  return (
    <>
      <div
        className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setDetailOpen(true)}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="font-medium text-sm truncate">{displayName}</span>
            {user.dni && (
              <span className="text-xs text-muted-foreground">DNI: {user.dni}</span>
            )}
          </div>
          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium shrink-0 ${payment.class}`}>
            {payment.label}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className={`${getRoleColor(user.role)} text-white text-xs`}>
            {getRoleLabel(user.role)}
          </Badge>
          {isPending && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Clock className="h-3 w-3" />
              Pendiente
            </Badge>
          )}
        </div>

        {canRegisterAndEdit && (
          <div
            className="flex flex-wrap items-center gap-2"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <PaymentForm
              user={user}
              gymId={user.gym_id || null}
              onPaymentRegistered={() => {
                queryClient.invalidateQueries({ queryKey: ["lastPayment", user.id] });
              }}
            />
            {isMastergym && canRegisterAndEdit && user.gym_id && (
              <DebtActionButton
                userId={user.id}
                gymId={user.gym_id}
                userName={displayName}
                debtInfo={debtInfo}
              />
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setEditOpen(true)}
              onMouseEnter={() => {
                queryClient.prefetchQuery({
                  queryKey: ["active-payments", user.id],
                  queryFn: async () => {
                    const { data, error } = await supabase
                      .from("payments")
                      .select("id, amount, description, paid_at, validity_days")
                      .eq("user_id", user.id)
                      .order("paid_at", { ascending: false });
                    if (error) throw error;
                    return (data || []).filter((p) => {
                      if (!p.paid_at) return false;
                      const expiration = addDays(new Date(p.paid_at), p.validity_days);
                      return isAfter(expiration, new Date());
                    });
                  },
                });
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {canPerformActions && !isPending && user.role !== "admin" && user.role !== "manager" && (
              <ResetPasswordButton
                userId={user.id}
                userName={displayName}
              />
            )}
            {canPerformActions && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onBanUser(user.id, !user.banned)}
                >
                  <Ban className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  className="ml-[50px]"
                  onClick={() => onDeleteClick(user)}
                >
                  <UserMinus className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <EditUserDialog user={user} open={editOpen} onOpenChange={setEditOpen} />
      <UserDetailDialog
        user={user}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEditClick={() => setEditOpen(true)}
      />
    </>
  );
};
